/**
 * SandboxDb — Browser-side SQLite database for the onboarding sandbox.
 *
 * Uses sql.js directly for browser SQLite with IndexedDB persistence.
 * No server connection — entirely client-side until claim.
 *
 * When LiteDB gets a browser-only entry point (Phase 4), this can be
 * replaced with `import { LiteDB } from '@litedb/core/browser'`.
 */

import type { SiteMeta, Page, Section, NavItem } from './site-schema';
import { siteSchema } from './site-schema';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SiteContent {
  site_meta: SiteMeta;
  pages: Page[];
  sections: Section[];
  nav_items: NavItem[];
}

type DataChangeCallback = () => void;

// ─── SandboxDb ───────────────────────────────────────────────────────────────

export class SandboxDb {
  private db: any = null; // sql.js Database instance
  private listeners: Set<DataChangeCallback> = new Set();
  private version = 0;
  private _ready = false;

  get ready(): boolean {
    return this._ready;
  }

  /**
   * Initialize the sandbox database.
   * Creates or resumes from IndexedDB.
   */
  async init(): Promise<void> {
    if (this._ready) return;

    // Dynamic import sql.js — handle both ESM and CJS export shapes
    const sqlJsModule = await import('sql.js');
    const initSqlJs = sqlJsModule.default || sqlJsModule;
    const SQL = await initSqlJs({
      locateFile: () => '/sql-wasm.wasm',
    });

    // Try to load from IndexedDB
    const saved = await this.loadFromIndexedDB();
    if (saved) {
      this.db = new SQL.Database(saved);
    } else {
      this.db = new SQL.Database();
      // Create tables from schema — run each statement individually
      const statements = siteSchema.toSQLAll().split(';').map(s => s.trim()).filter(Boolean);
      for (const stmt of statements) {
        this.db.run(stmt);
      }
      await this.saveToIndexedDB();
    }

    this._ready = true;
  }

  /**
   * Load a template from a static JSON file.
   * Replaces current database contents.
   */
  async loadTemplate(slug: string): Promise<void> {
    if (!this.db) throw new Error('SandboxDb not initialized');

    const response = await fetch(`/templates/${slug}.json`);
    if (!response.ok) throw new Error(`Template "${slug}" not found`);
    const data: SiteContent = await response.json();

    // Clear existing data
    this.db.run('DELETE FROM sections');
    this.db.run('DELETE FROM nav_items');
    this.db.run('DELETE FROM pages');
    this.db.run('DELETE FROM site_meta');

    // Insert site meta
    const m = data.site_meta;
    this.db.run(
      `INSERT INTO site_meta (id, name, tagline, business_type, branding, status, template_slug, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [this.uuid(), m.name, m.tagline, m.business_type, JSON.stringify(m.branding), m.status, m.template_slug, this.now(), this.now()],
    );

    // Get the inserted site_meta id
    const metaRow = this.db.exec('SELECT id FROM site_meta LIMIT 1');
    const siteId = metaRow[0]?.values[0]?.[0] as string;

    // Insert pages
    for (const p of data.pages) {
      const pageId = this.uuid();
      this.db.run(
        `INSERT INTO pages (id, site_id, title, slug, sort_order, is_home, seo_title, seo_description, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [pageId, siteId, p.title, p.slug, p.sort_order, p.is_home ? 1 : 0, p.seo_title, p.seo_description, this.now(), this.now()],
      );

      // Insert sections for this page
      const pageSections = data.sections.filter((s) => s.page_id === p.id);
      for (const s of pageSections) {
        this.db.run(
          `INSERT INTO sections (id, page_id, type, sort_order, content, style, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
          [this.uuid(), pageId, s.type, s.sort_order, JSON.stringify(s.content), s.style ? JSON.stringify(s.style) : null, this.now(), this.now()],
        );
      }
    }

    // Insert nav items
    for (const n of data.nav_items) {
      this.db.run(
        `INSERT INTO nav_items (id, site_id, label, href, sort_order, is_cta, created_at, updated_at)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
        [this.uuid(), siteId, n.label, n.href, n.sort_order, n.is_cta ? 1 : 0, this.now(), this.now()],
      );
    }

    await this.saveToIndexedDB();
    this.notifyChange();
  }

  /**
   * Get all site content as structured JSON (for claim/export).
   */
  async exportContent(): Promise<SiteContent | null> {
    if (!this.db) return null;

    const meta = this.queryOne<SiteMeta>('SELECT * FROM site_meta LIMIT 1');
    if (!meta) return null;

    return {
      site_meta: { ...meta, branding: this.parseJson(meta.branding) },
      pages: this.queryAll<Page>('SELECT * FROM pages ORDER BY sort_order'),
      sections: this.queryAll<Section>('SELECT * FROM sections ORDER BY sort_order').map((s) => ({
        ...s,
        content: this.parseJson(s.content),
        style: this.parseJson(s.style),
      })),
      nav_items: this.queryAll<NavItem>('SELECT * FROM nav_items ORDER BY sort_order'),
    };
  }

  /**
   * Get the current site meta.
   */
  async getSiteMeta(): Promise<SiteMeta | null> {
    if (!this.db) return null;
    const meta = this.queryOne<SiteMeta>('SELECT * FROM site_meta LIMIT 1');
    if (!meta) return null;
    return { ...meta, branding: this.parseJson(meta.branding) };
  }

  /**
   * Update site meta fields.
   */
  async updateSiteMeta(data: Partial<SiteMeta>): Promise<void> {
    if (!this.db) return;
    const meta = this.queryOne<any>('SELECT id FROM site_meta LIMIT 1');
    if (!meta) return;

    const sets: string[] = [];
    const vals: any[] = [];
    for (const [key, val] of Object.entries(data)) {
      if (key === 'id' || key === 'created_at') continue;
      sets.push(`${key} = ?`);
      vals.push(typeof val === 'object' ? JSON.stringify(val) : val);
    }
    if (sets.length === 0) return;

    sets.push('updated_at = ?');
    vals.push(this.now());
    vals.push(meta.id);

    this.db.run(`UPDATE site_meta SET ${sets.join(', ')} WHERE id = ?`, vals);
    await this.saveToIndexedDB();
    this.notifyChange();
  }

  /**
   * Get all sections for the home page, ordered.
   */
  async getHomeSections(): Promise<Section[]> {
    if (!this.db) return [];

    let pageId: string | null = null;

    // Try is_home = 1
    const homePage = this.queryOne<Page>('SELECT id FROM pages WHERE is_home = 1 LIMIT 1');
    if (homePage) {
      pageId = homePage.id;
    } else {
      // Fallback: first page
      const firstPage = this.queryOne<Page>('SELECT id FROM pages ORDER BY sort_order LIMIT 1');
      if (firstPage) pageId = firstPage.id;
    }

    if (!pageId) return [];

    return this.queryAll<Section>(
      'SELECT * FROM sections WHERE page_id = ? ORDER BY sort_order',
      [pageId],
    ).map((s) => ({
      ...s,
      content: this.parseJson(s.content),
      style: this.parseJson(s.style),
    }));
  }

  /**
   * Get nav items for the site.
   */
  async getNavItems(): Promise<NavItem[]> {
    if (!this.db) return [];
    return this.queryAll<NavItem>('SELECT * FROM nav_items ORDER BY sort_order');
  }

  /**
   * Add a section to the home page.
   */
  async addSection(type: string, content: Record<string, unknown>, sortOrder?: number): Promise<Section | null> {
    if (!this.db) return null;

    const homePage = this.queryOne<Page>('SELECT id FROM pages WHERE is_home = 1 LIMIT 1');
    const pageId = homePage?.id;
    if (!pageId) return null;

    const countResult = this.execParams('SELECT COUNT(*) as cnt FROM sections WHERE page_id = ?', [pageId]);
    const order = sortOrder ?? (countResult[0]?.cnt as number ?? 0);
    const id = this.uuid();
    const now = this.now();

    this.db.run(
      `INSERT INTO sections (id, page_id, type, sort_order, content, style, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, pageId, type, order, JSON.stringify(content), null, now, now],
    );

    await this.saveToIndexedDB();
    this.notifyChange();

    return {
      id,
      page_id: pageId,
      type: type as any,
      sort_order: order,
      content,
      style: null,
      created_at: now,
      updated_at: now,
    };
  }

  /**
   * Update a section's content.
   */
  async updateSection(id: string, content: Record<string, unknown>): Promise<void> {
    if (!this.db) return;
    this.db.run(
      'UPDATE sections SET content = ?, updated_at = ? WHERE id = ?',
      [JSON.stringify(content), this.now(), id],
    );
    await this.saveToIndexedDB();
    this.notifyChange();
  }

  /**
   * Reset the sandbox — clear all data.
   */
  async reset(): Promise<void> {
    if (!this.db) return;
    this.db.run('DELETE FROM sections');
    this.db.run('DELETE FROM nav_items');
    this.db.run('DELETE FROM pages');
    this.db.run('DELETE FROM site_meta');
    await this.saveToIndexedDB();
    this.notifyChange();
  }

  /**
   * Register a callback for data changes.
   */
  onDataChange(cb: DataChangeCallback): () => void {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /**
   * Close the database connection.
   */
  async close(): Promise<void> {
    if (this.db) {
      await this.saveToIndexedDB();
      this.db.close();
      this.db = null;
      this._ready = false;
    }
  }

  // ─── Private helpers ─────────────────────────────────────────────────────

  private queryOne<T>(sql: string, params?: any[]): T | null {
    const rows = this.execParams(sql, params);
    if (rows.length === 0) return null;
    return rows[0] as T;
  }

  private queryAll<T>(sql: string, params?: any[]): T[] {
    return this.execParams(sql, params) as T[];
  }

  /**
   * Execute a query with parameterized bindings using prepared statements.
   * sql.js's db.exec() does NOT support params — must use stmt.bind().
   */
  private execParams(sql: string, params?: any[]): Record<string, any>[] {
    const stmt = this.db.prepare(sql);
    if (params && params.length > 0) {
      stmt.bind(params);
    }
    const rows: Record<string, any>[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject();
      rows.push(row);
    }
    stmt.free();
    return rows;
  }

  private parseJson(val: any): any {
    if (val === null || val === undefined) return null;
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch {
        return val;
      }
    }
    return val;
  }

  private uuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private now(): string {
    return new Date().toISOString();
  }

  private async saveToIndexedDB(): Promise<void> {
    if (typeof indexedDB === 'undefined' || !this.db) return;
    const data = this.db.export();
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('wiserweb_sandbox', 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('db');
      };
      request.onsuccess = () => {
        const tx = request.result.transaction('db', 'readwrite');
        tx.objectStore('db').put(data, 'sqlite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async loadFromIndexedDB(): Promise<Uint8Array | null> {
    if (typeof indexedDB === 'undefined') return null;
    return new Promise((resolve) => {
      const request = indexedDB.open('wiserweb_sandbox', 1);
      request.onupgradeneeded = () => {
        request.result.createObjectStore('db');
      };
      request.onsuccess = () => {
        try {
          const tx = request.result.transaction('db', 'readonly');
          const getRequest = tx.objectStore('db').get('sqlite');
          getRequest.onsuccess = () => resolve(getRequest.result || null);
          getRequest.onerror = () => resolve(null);
        } catch {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  }

  private notifyChange(): void {
    this.version++;
    for (const cb of this.listeners) {
      try {
        cb();
      } catch (e) {
        console.error('SandboxDb change listener error:', e);
      }
    }
  }
}
